import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Alert,
  CircularProgress, List, ListItem, ListItemText, ListItemAvatar, Avatar,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import {
  Work as WorkIcon, Send as SendIcon, Business as BusinessIcon
} from '@mui/icons-material';
import { useMutation, useQuery } from 'react-query';
import apiClient from '../../request/http-common';
import { useSelector } from 'react-redux';

const ApplyForJob = () => {
  const user = useSelector((state) => state.user.user);
  const [selectedJob, setSelectedJob] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch approved jobs
  const { data: approvedJobs, isLoading, error: fetchError } = useQuery(
    'approvedJobs',
    async () => {
      const response = await apiClient.get('/api/jobs/approved');
      return response.data;
    },
    {
      refetchInterval: 60000,
      onError: (error) => {
        console.error('Fetch Jobs API Error:', error);
      }
    }
  );

  const applicationMutation = useMutation(
    async (applicationData) => {
      const response = await apiClient.post('/api/job-applications', applicationData);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setSuccess('Application submitted successfully! It is pending admin review.');
        setSelectedJob(null);
        setCoverLetter('');
        setDialogOpen(false);
        setError('');
      },
      onError: (error) => {
        setError(error.response?.data?.message || 'Failed to submit application');
        setSuccess('');
      }
    }
  );

  const handleApply = (job) => {
    setSelectedJob(job);
    setCoverLetter('');
    setError('');
    setSuccess('');
    setDialogOpen(true);
  };

  const handleSubmitApplication = () => {
    if (!coverLetter.trim()) {
      setError('Please provide a cover letter');
      return;
    }

    applicationMutation.mutate({
      job_id: selectedJob.id,
      cover_letter: coverLetter
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load jobs: {fetchError.message}
        </Alert>
      </Box>
    );
  }

  const jobs = approvedJobs?.jobs || [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <WorkIcon sx={{ fontSize: 32, color: '#F7931A' }} />
        <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
          Available Jobs
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {jobs.length === 0 ? (
        <Card sx={{
          background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
          border: '1px solid #2B3139',
          borderRadius: '12px'
        }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <WorkIcon sx={{ fontSize: 64, color: '#6C7293', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 1 }}>
              No Jobs Available
            </Typography>
            <Typography variant="body2" sx={{ color: '#6C7293' }}>
              Check back later for new job opportunities.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {jobs.map((job) => (
            <ListItem
              key={job.id}
              sx={{
                border: '1px solid #2B3139',
                borderRadius: 1,
                mb: 2,
                background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
                '&:hover': {
                  borderColor: '#F7931A'
                }
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: '#F7931A' }}>
                  <WorkIcon />
                </Avatar>
              </ListItemAvatar>

              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                      {job.job_title}
                    </Typography>
                    <Chip
                      label={job.company_name}
                      size="small"
                      sx={{ background: 'rgba(247, 147, 26, 0.2)', color: '#F7931A' }}
                    />
                    <Chip
                      label={job.job_type}
                      size="small"
                      sx={{ background: 'rgba(2, 192, 118, 0.2)', color: '#02C076' }}
                    />
                    <Chip
                      label={job.experience_level}
                      size="small"
                      sx={{ background: 'rgba(59, 130, 246, 0.2)', color: '#3B82F6' }}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Location:</strong> {job.location || 'Not specified'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Salary:</strong> {job.salary_range || 'Not specified'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Description:</strong> {job.job_description?.substring(0, 150)}...
                    </Typography>
                    {job.application_deadline && (
                      <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                        <strong>Deadline:</strong> {new Date(job.application_deadline).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                }
              />

              <Button
                variant="contained"
                onClick={() => handleApply(job)}
                startIcon={<SendIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #F7931A 0%, #FFD700 100%)',
                  color: '#000',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #E8841A 0%, #E6C200 100%)'
                  }
                }}
              >
                Apply
              </Button>
            </ListItem>
          ))}
        </List>
      )}

      {/* Application Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#FFFFFF', background: '#1a1a1a' }}>
          Apply for {selectedJob?.job_title}
        </DialogTitle>
        <DialogContent sx={{ background: '#1a1a1a', color: '#FFFFFF' }}>
          {selectedJob && (
            <Box>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Typography variant="h6" sx={{ mb: 2, color: '#F7931A' }}>
                {selectedJob.company_name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#B7BDC6' }}>
                {selectedJob.job_description}
              </Typography>

              {selectedJob.requirements && (
                <>
                  <Typography variant="body1" sx={{ mb: 1, mt: 2 }}>
                    <strong>Requirements:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: '#B7BDC6', whiteSpace: 'pre-wrap' }}>
                    {selectedJob.requirements}
                  </Typography>
                </>
              )}

              <Divider sx={{ borderColor: '#2B3139', my: 2 }} />

              <TextField
                fullWidth
                multiline
                rows={6}
                label="Cover Letter *"
                value={coverLetter}
                onChange={(e) => setCoverLetter(e.target.value)}
                required
                placeholder="Write your cover letter here..."
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#6C7293' },
                    '&:hover fieldset': { borderColor: '#F7931A' },
                  },
                  '& .MuiInputLabel-root': { color: '#6C7293' },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ background: '#1a1a1a', p: 2, gap: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ color: '#6C7293' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitApplication}
            variant="contained"
            disabled={applicationMutation.isLoading}
            startIcon={applicationMutation.isLoading ? <CircularProgress size={20} /> : <SendIcon />}
            sx={{
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
              }
            }}
          >
            {applicationMutation.isLoading ? 'Submitting...' : 'Submit Application'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApplyForJob;

